import type { ApiClient } from './ApiClient';

// Forme alignée sur les routes PAR-04 (apps/api/src/routes/
// declaredChildGuardians.ts) — seule source de vérité. `status` TOUJOURS
// dérivé côté serveur (jamais stocké), même convention que `Invitation`
// (AUT-08, invitation.ts).
export type GuardianInvitationStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
export type GuardianRelationship = 'mother' | 'father' | 'guardian';

export interface DeclaredChildGuardian {
  id: string;
  email: string;
  relationship: GuardianRelationship;
  isPrimaryPayer: boolean;
  status: GuardianInvitationStatus;
  createdAt: string;
}

export interface DeclaredChildOriginalParent {
  email: string;
  isPrimaryPayer: boolean;
}

export interface ListDeclaredChildGuardiansResponse {
  originalParent: DeclaredChildOriginalParent;
  guardians: DeclaredChildGuardian[];
}

export function listDeclaredChildGuardians(
  client: ApiClient,
  declaredChildId: string,
  accessToken: string,
): Promise<ListDeclaredChildGuardiansResponse> {
  return client.get<ListDeclaredChildGuardiansResponse>(`/declared-children/${declaredChildId}/guardians`, { accessToken });
}

export interface InviteDeclaredChildGuardianPayload {
  email: string;
  relationship: GuardianRelationship;
}

export interface InviteDeclaredChildGuardianResponse {
  guardian: DeclaredChildGuardian;
}

export function inviteDeclaredChildGuardian(
  client: ApiClient,
  declaredChildId: string,
  payload: InviteDeclaredChildGuardianPayload,
  accessToken: string,
): Promise<InviteDeclaredChildGuardianResponse> {
  return client.post<InviteDeclaredChildGuardianResponse>(`/declared-children/${declaredChildId}/guardians`, payload, { accessToken });
}

export interface RevokeDeclaredChildGuardianResponse {
  guardian: DeclaredChildGuardian;
}

// Uniquement une invitation encore PENDING — voir apps/api/src/routes/
// declaredChildGuardians.ts pour le garde-fou "accès déjà accepté", qui
// renvoie 409 `cannot_revoke_accepted_guardian` (révocation manuelle par le
// support requise dans ce cas, jamais exposée ici).
export function revokeDeclaredChildGuardian(
  client: ApiClient,
  guardianId: string,
  accessToken: string,
): Promise<RevokeDeclaredChildGuardianResponse> {
  return client.post<RevokeDeclaredChildGuardianResponse>(`/declared-child-guardians/${guardianId}/revoke`, {}, { accessToken });
}

export interface SetGuardianPrimaryPayerResponse {
  guardian: DeclaredChildGuardian;
}

// `isPrimaryPayer: true` rétrograde automatiquement tout autre payeur
// principal actif pour le même enfant (bascule atomique côté serveur) ;
// `isPrimaryPayer: false` sur le payeur courant revient à l'état par défaut
// implicite (le parent créateur redevient payeur principal).
export function setGuardianPrimaryPayer(
  client: ApiClient,
  guardianId: string,
  isPrimaryPayer: boolean,
  accessToken: string,
): Promise<SetGuardianPrimaryPayerResponse> {
  return client.patch<SetGuardianPrimaryPayerResponse>(`/declared-child-guardians/${guardianId}/primary-payer`, { isPrimaryPayer }, { accessToken });
}

export interface AcceptGuardianInvitationResponse {
  declaredChildId: string;
}

// Requiert un utilisateur déjà authentifié AVEC un profil parent existant
// (voir `400 parent_profile_required` côté serveur) — pas de second
// paramètre "email" : le serveur compare le token à l'utilisateur COURANT,
// même contrat que `acceptInvitation()` (AUT-08).
export function acceptGuardianInvitation(client: ApiClient, token: string, accessToken: string): Promise<AcceptGuardianInvitationResponse> {
  return client.post<AcceptGuardianInvitationResponse>('/declared-child-guardian-invitations/accept', { token }, { accessToken });
}
